import { ReactNode, useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { MoreHoriz } from '@mui/icons-material';
import styled from '@emotion/styled';
import { uiTheme as t } from './theme';

export interface ActionMenuOption {
  label: string;
  icon?: ReactNode;
  /** `'danger'` pinta la opción en rojo, para lo que destruye. */
  tone?: 'default' | 'danger';
  disabled?: boolean;
  onSelect: () => void;
}

interface ActionMenuProps {
  /** Texto accesible del disparador. Nunca se ve; lo lee el lector de pantalla. */
  label: string;
  options: ActionMenuOption[];
}

const Trigger = styled.button({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  border: 'none',
  borderRadius: t.radius.xs,
  background: 'transparent',
  color: t.color.muted,
  cursor: 'pointer',
  transition: 'color 0.15s ease, background 0.15s ease',
  '& .MuiSvgIcon-root': { fontSize: 20 },
  '&:hover': { color: t.color.title, background: t.color.segmentTrack },
});

const Item = styled.span<{ tone: 'default' | 'danger' }>(({ tone }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  fontFamily: t.font,
  fontSize: 13.5,
  color: tone === 'danger' ? t.color.danger : t.color.text,
  '& svg': { fontSize: 17 },
}));

/**
 * Molécula: menú de acciones colgado de un disparador discreto.
 *
 * Sin opciones no se pinta nada. Un botón que abre un menú vacío es peor que la
 * ausencia del botón: promete algo y no lo cumple, y en esta vista las opciones
 * dependen de los permisos de quien mira.
 */
const ActionMenu = ({ label, options }: ActionMenuProps) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (options.length === 0) return null;

  const close = () => setAnchor(null);

  return (
    <>
      <Trigger type="button" aria-label={label} onClick={event => setAnchor(event.currentTarget)}>
        <MoreHoriz />
      </Trigger>
      {anchor ? (
        <Menu
          anchorEl={anchor}
          open
          onClose={close}
          PaperProps={{
            sx: {
              borderRadius: t.radius.xs,
              border: `1px solid ${t.color.border}`,
              boxShadow: '0 8px 22px rgba(16, 24, 40, 0.12)',
            },
          }}
        >
          {options.map(option => (
            <MenuItem
              key={option.label}
              disabled={option.disabled}
              onClick={() => {
                if (option.disabled) return;
                option.onSelect();
                close();
              }}
            >
              <Item tone={option.tone ?? 'default'}>
                {option.icon}
                {option.label}
              </Item>
            </MenuItem>
          ))}
        </Menu>
      ) : null}
    </>
  );
};

export default ActionMenu;
